import { Link } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import type { SeoLink } from "@/lib/seo-links";

/**
 * Renders a column-friendly grid of internal SEO links as real <a> tags
 * (TanStack Link renders an <a>) so crawlers see them in the SSR HTML.
 * Styled after the reference CityFinder link-list pattern.
 */
export function SeoLinkList({
  heading,
  links,
  columns = 3,
  icon: Icon,
  className = "",
}: {
  heading?: string;
  links: SeoLink[];
  columns?: 2 | 3 | 4;
  /** Optional leading icon per link (e.g. MapPin for city lists). */
  icon?: LucideIcon;
  className?: string;
}) {
  if (!links?.length) return null;
  const colClass =
    columns === 4
      ? "sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
      : columns === 3
      ? "sm:grid-cols-2 md:grid-cols-3"
      : "sm:grid-cols-2";
  return (
    <section className={className}>
      {heading ? (
        <h2 className="font-display text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          {heading}
        </h2>
      ) : null}
      <ul className={`mt-4 grid grid-cols-1 ${colClass} gap-x-6 gap-y-3`}>
        {links.map((l) => (
          <li key={l.href + l.label}>
            <Link
              to={l.href}
              rel={l.rel}
              className="inline-flex items-center gap-2 text-[14px] text-muted-foreground hover:text-primary transition-colors"
            >
              {Icon ? <Icon className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden /> : null}
              <span>{l.label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
